import {useDisclosure, VStack} from '@chakra-ui/react';
import {skipToken} from '@reduxjs/toolkit/query/react';
import {useGetCourtsForEventQuery, useGetLeaguesForEventQuery, useGetTeamsForEventQuery,} from 'app/api';
import Page from 'components/Page/Page';
import {useEventContext} from 'contexts/EventContext';
import {useOrganizationContext} from 'contexts/OrganizationContext';
import {useParams} from 'react-router';
import {useGameNotification} from './hooks/useGameNotification';
import {usePlayoffSchedule} from './hooks/usePlayoffSchedule';
import {useScheduleGameForm} from './hooks/useScheduleGameForm';
import {ScheduleGameModal} from './ScheduleGameModal';
import {PlayoffBracketDisplay} from './PlayoffBracketDisplay';
import {PlayoffSchedulingHeader} from './PlayoffSchedlingHeader';
import {PlayoffInfoModal} from './PlayoffInfoModal';
import {GameView} from '../../../types';

const PlayoffScheduling: React.FC = () => {
  const {eventId} = useParams();
  const {currentOrg} = useOrganizationContext();
  const {currentEvent, canPerformEventAction} = useEventContext();

  const parsedEventId = eventId ? parseInt(eventId) : 0;

  const {data: leagues} = useGetLeaguesForEventQuery(
    parsedEventId || skipToken
  );
  const {data: teams} = useGetTeamsForEventQuery(
    parsedEventId || skipToken
  );
  const {data: courts} = useGetCourtsForEventQuery(
    parsedEventId || skipToken
  );

  const {
    selectedLeague,
    setSelectedLeague,
    showPlayoffSchedule,
    refreshSchedule,
    isDoubleElimination,
  } = usePlayoffSchedule({
    eventId: parsedEventId,
    leagues,
  });

  const {sendGameNotification} = useGameNotification();

  const handleSuccess = async (gameView: GameView) => {
    if (selectedLeague) {
      await refreshSchedule(selectedLeague);
    }

    await sendGameNotification(parsedEventId, gameView);

    const gameUrl = `/organizations/${currentOrg?.id}/events/${parsedEventId}/scoringv2/${gameView.id}`;
    window.open(gameUrl, '_blank');

    onScheduleModalClose();
  };

  const scheduleGameForm = useScheduleGameForm({
    eventId: parsedEventId,
    courts,
    selectedLeague,
    onSuccess: handleSuccess,
  });

  const {
    isOpen: isScheduleModalOpen,
    onOpen: onScheduleModalOpen,
    onClose: onScheduleModalClose,
  } = useDisclosure();

  const {
    isOpen: isInfoOpen,
    onOpen: onInfoOpen,
    onClose: onInfoClose,
  } = useDisclosure();

  const handleScheduleClick = () => {
    scheduleGameForm.initializeForm();
    onScheduleModalOpen();
  };

  const handleModalClose = () => {
    scheduleGameForm.resetForm();
    onScheduleModalClose();
  };

  const canSchedule = canPerformEventAction('SCORE_KEEPER');

  return (
    <Page display="flex" flexDir="column" p={5}>
      <VStack spacing={4} w="100%" h="100%">
        <PlayoffSchedulingHeader
          eventName={currentEvent?.name}
          canSchedule={canSchedule}
          onScheduleClick={handleScheduleClick}
          onInfoClick={onInfoOpen}
        />

        <PlayoffBracketDisplay
          leagues={leagues}
          teams={teams}
          schedule={showPlayoffSchedule}
          isDoubleElimination={isDoubleElimination}
          selectedLeague={selectedLeague}
          onLeagueChange={setSelectedLeague}
        />
      </VStack>

      <ScheduleGameModal
        isOpen={isScheduleModalOpen}
        onClose={handleModalClose}
        leagues={leagues}
        courts={courts}
        selectedLeague={scheduleGameForm.selectedLeagueForSchedule}
        onLeagueChange={scheduleGameForm.setSelectedLeagueForSchedule}
        selectedCourt={scheduleGameForm.selectedCourtId}
        onCourtChange={scheduleGameForm.setSelectedCourtId}
        startTime={scheduleGameForm.startTime}
        onStartTimeChange={scheduleGameForm.handleStartTimeChange}
        endTime={scheduleGameForm.endTime}
        onEndTimeChange={scheduleGameForm.handleEndTimeChange}
        timeError={scheduleGameForm.timeError}
        isLoading={scheduleGameForm.isEnqueueing}
        onSubmit={scheduleGameForm.handleSubmit}
      />

      <PlayoffInfoModal isOpen={isInfoOpen} onClose={onInfoClose}/>
    </Page>
  );
};

export default PlayoffScheduling;
